/**
 * Addons Thunks
 *
 * Async thunks for flight addons operations (addons, meals, baggage, coupons)
 */
import { createAsyncThunk } from '@reduxjs/toolkit';
import type {
  FlightsAddonsComponentData,
  FlightsAddonMealsComponentData,
  FlightsAddonBaggageComponentData,
  FlightsCouponsComponentData,
  FlightsSeatsComponentData,
  FetchSeatsRequestParams,
} from '@contracts';
import {
  fetchAddons as fetchAddonsApi,
  selectAddon as selectAddonApi,
  fetchMeals as fetchMealsApi,
  fetchBaggage as fetchBaggageApi,
  fetchCoupons as fetchCouponsApi,
  fetchSeats as fetchSeatsApi,
  FetchAddonsError,
  AddonSelectionError,
  FetchMealsError,
  FetchBaggageError,
  FetchCouponsError,
  FetchSeatsError,
} from '../api/addonsApi';
import type {
  FetchAddonsRequestParams,
  AddonSelectionRequestParams,
  AddonSelectionRequestBody,
  AddonSelectionResponse,
  FetchMealsRequestParams,
  FetchBaggageRequestParams,
  FetchCouponsRequestParams,
} from '../types';
import type { RootState } from '@/store/store';
import { getOrCreateSessionId } from '@/shared/utils';
import { getUUID } from '@/shared/utils/appCookies';

// ============================================
// Thunks
// ============================================

/**
 * Fetch available addons
 */
export const fetchAddonsThunk = createAsyncThunk<
  FlightsAddonsComponentData,
  FetchAddonsRequestParams,
  { rejectValue: string }
>('flights/fetchAddons', async (params, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const data = await fetchAddonsApi({ ...params, sessionId, userId });
    return data;
  } catch (error) {
    if (error instanceof FetchAddonsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch addons');
  }
});

/**
 * Select an addon - updates only the changed fields instead of refetching
 * This thunk:
 * 1. Calls selectAddon API to select/deselect addon
 * 2. Returns selection response with itemCode and isSelected for partial update
 * 3. Redux slice will update only the matching addon item's isSelected flag
 */
interface SelectAddonParams {
  requestParams: AddonSelectionRequestParams;
  body: AddonSelectionRequestBody;
}

interface SelectAddonResponse {
  selectionResponse: AddonSelectionResponse & { ctaList?: unknown; addons?: unknown };
  itemCode: string;
  sectorId: string;
  isSelected: boolean;
}

export const selectAddonThunk = createAsyncThunk<
  SelectAddonResponse,
  SelectAddonParams,
  { rejectValue: string }
>('flights/selectAddon', async ({ requestParams, body }, { rejectWithValue }) => {
  try {
    // Call selectAddon API - response includes fareSummary and potentially ctaList/addons
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const selectionResponse = await selectAddonApi({ ...requestParams, sessionId, userId }, body);

    return {
      selectionResponse,
      itemCode: body.itemCode,
      sectorId: body.sectorId,
      isSelected: body.isSelected,
    };
  } catch (error) {
    if (error instanceof AddonSelectionError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to select addon');
  }
});

/**
 * Fetch available meals
 * Automatically includes sessionId and userId from session utils
 */
export const fetchMealsThunk = createAsyncThunk<
  FlightsAddonMealsComponentData,
  FetchMealsRequestParams,
  { rejectValue: string }
>('flights/fetchMeals', async (params, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const data = await fetchMealsApi({
      ...params,
      sessionId,
      userId,
    });
    return data;
  } catch (error) {
    if (error instanceof FetchMealsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch meals');
  }
});

/**
 * Fetch available baggage options
 */
export const fetchBaggageThunk = createAsyncThunk<
  FlightsAddonBaggageComponentData,
  FetchBaggageRequestParams,
  { rejectValue: string }
>('flights/fetchBaggage', async (params, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const data = await fetchBaggageApi({
      ...params,
      sessionId,
      userId,
    });
    return data;
  } catch (error) {
    if (error instanceof FetchBaggageError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch baggage');
  }
});

/**
 * Fetch available coupons
 */
export const fetchCouponsThunk = createAsyncThunk<
  FlightsCouponsComponentData,
  FetchCouponsRequestParams,
  { rejectValue: string }
>('flights/fetchCoupons', async (params, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const data = await fetchCouponsApi({ ...params, sessionId, userId });
    return data;
  } catch (error) {
    if (error instanceof FetchCouponsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch coupons');
  }
});

/**
 * Fetch available seats
 * Automatically includes sessionId and userId from session utils
 */
export const fetchSeatsThunk = createAsyncThunk<
  FlightsSeatsComponentData,
  FetchSeatsRequestParams,
  { state: RootState; rejectValue: string }
>('flights/fetchSeats', async (params, { rejectWithValue }) => {
  try {
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const data = await fetchSeatsApi({
      ...params,
      sessionId,
      userId,
    });
    return data;
  } catch (error) {
    if (error instanceof FetchSeatsError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to fetch seats');
  }
});

/**
 * Select a coupon - updates only the changed fields instead of refetching
 * This thunk:
 * 1. Calls selectAddon API to apply/remove coupon
 * 2. Returns selection response with itemCode and isSelected for partial update
 * 3. Redux slice will update only the matching coupon item's isSelected flag
 */
interface SelectCouponParams {
  requestParams: AddonSelectionRequestParams;
  body: AddonSelectionRequestBody;
}

interface SelectCouponResponse {
  selectionResponse: AddonSelectionResponse & { ctaList?: unknown; coupons?: unknown };
  itemCode: string;
  isSelected: boolean;
}

export const selectCouponThunk = createAsyncThunk<
  SelectCouponResponse,
  SelectCouponParams,
  { rejectValue: string }
>('flights/selectCoupon', async ({ requestParams, body }, { rejectWithValue }) => {
  try {
    // Call selectAddon API - response includes fareSummary and potentially couponData/coupons
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const selectionResponse = await selectAddonApi({ ...requestParams, sessionId, userId }, body);

    return {
      selectionResponse,
      itemCode: body.itemCode,
      isSelected: body.isSelected,
    };
  } catch (error) {
    if (error instanceof AddonSelectionError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to select coupon');
  }
});

/**
 * Select meal - updates only the changed fields instead of refetching
 * This thunk:
 * 1. Calls selectAddon API to select/deselect meal
 * 2. Returns selection response with itemCode and isSelected for partial update
 * 3. Redux slice will update only the matching meal item's isSelected flag
 */
interface SelectMealParams {
  requestParams: AddonSelectionRequestParams;
  body: AddonSelectionRequestBody;
}

interface SelectMealResponse {
  selectionResponse: AddonSelectionResponse & { ctaList?: unknown; meals?: unknown };
  itemCode: string;
  sectorId: string;
  isSelected: boolean;
}

export const selectMealThunk = createAsyncThunk<
  SelectMealResponse,
  SelectMealParams,
  { rejectValue: string }
>('flights/selectMeal', async ({ requestParams, body }, { rejectWithValue }) => {
  try {
    // Call selectAddon API - response includes fareSummary and potentially ctaList
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const selectionResponse = await selectAddonApi({ ...requestParams, sessionId, userId }, body);

    return {
      selectionResponse,
      itemCode: body.itemCode,
      sectorId: body.sectorId,
      isSelected: body.isSelected,
    };
  } catch (error) {
    if (error instanceof AddonSelectionError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to select meal');
  }
});

/**
 * Select baggage - updates only the changed fields instead of refetching
 * This thunk:
 * 1. Calls selectAddon API to select/deselect baggage
 * 2. Returns selection response with itemCode and isSelected for partial update
 * 3. Redux slice will update only the matching baggage item's isSelected flag
 */
interface SelectBaggageParams {
  requestParams: AddonSelectionRequestParams;
  body: AddonSelectionRequestBody;
}

interface SelectBaggageResponse {
  selectionResponse: AddonSelectionResponse & { ctaList?: unknown; baggage?: unknown };
  itemCode: string;
  sectorId: string;
  isSelected: boolean;
}

export const selectBaggageThunk = createAsyncThunk<
  SelectBaggageResponse,
  SelectBaggageParams,
  { rejectValue: string }
>('flights/selectBaggage', async ({ requestParams, body }, { rejectWithValue }) => {
  try {
    // Call selectAddon API - response includes fareSummary and potentially ctaList
    const sessionId = getOrCreateSessionId();
    const userId = getUUID();
    const selectionResponse = await selectAddonApi({ ...requestParams, sessionId, userId }, body);

    return {
      selectionResponse,
      itemCode: body.itemCode,
      sectorId: body.sectorId,
      isSelected: body.isSelected,
    };
  } catch (error) {
    if (error instanceof AddonSelectionError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Failed to select baggage');
  }
});
